import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { offlineDb, type SyncIssue } from '../../offline/db'
import { enqueue } from '../../offline/queueOps'
import type { OfflineOperation } from '../../offline/db'

interface Props {
  issue: SyncIssue
  onClose: () => void
}

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return '—'
  if (val instanceof Array) return `[${val.length} items]`
  if (typeof val === 'object') return JSON.stringify(val)
  return String(val)
}

function FieldDiff({ field, client, server }: { field: string; client: unknown; server: unknown }) {
  const isDiff = JSON.stringify(client) !== JSON.stringify(server)
  return (
    <tr className={isDiff ? 'bg-theme-warning/10' : ''}>
      <td className="py-1 pr-3 text-xs font-medium text-theme-text-muted whitespace-nowrap">{field}</td>
      <td className={`py-1 pr-3 text-xs font-mono ${isDiff ? 'text-theme-warning font-bold' : 'text-theme-text'}`}>
        {formatValue(client)}
      </td>
      <td className="py-1 pr-1 shrink-0 text-theme-text-muted">
        {isDiff && <FontAwesomeIcon icon={faArrowRight} className="text-[0.6rem]" />}
      </td>
      <td className={`py-1 text-xs font-mono ${isDiff ? 'text-theme-success font-bold' : 'text-theme-text'}`}>
        {formatValue(server)}
      </td>
    </tr>
  )
}

function serverEntityId(issue: SyncIssue): number | undefined {
  const s = issue.serverState as Record<string, unknown> | null
  if (!s) return undefined
  const key = `${issue.entity}Id`
  return s[key] as number | undefined
}

async function keepMine(issue: SyncIssue, onClose: () => void) {
  const server = issue.serverState as Record<string, unknown> | null
  const serverRowVersion = server?.rowVersion as string | undefined
  const resolvedEntityId = issue.entityId ?? serverEntityId(issue)
  const resolvedOperation: OfflineOperation = issue.operation === 'create' && resolvedEntityId != null ? 'update' : issue.operation as OfflineOperation
  await enqueue({
    entity: issue.entity,
    operation: resolvedOperation,
    entityId: resolvedEntityId,
    shiftId: issue.shiftId ?? 0,
    rowVersion: serverRowVersion,
    data: issue.clientData,
  })
  await offlineDb.syncIssues.delete(issue.id!)
  onClose()
}

async function keepTheirs(issue: SyncIssue, onClose: () => void) {
  await offlineDb.syncIssues.delete(issue.id!)
  onClose()
}

const ConflictResolutionModal = ({ issue, onClose }: Props) => {
  const clientObj = issue.clientData as Record<string, unknown> | null
  const serverObj = issue.serverState as Record<string, unknown> | null

  const allKeys = Array.from(new Set([
    ...Object.keys(clientObj ?? {}),
    ...Object.keys(serverObj ?? {}),
  ])).filter(k => k !== 'rowVersion')

  const entityLabel = issue.entity === 'diggingPattern' ? 'digging location'
    : issue.entity === 'patternBlast' ? 'blast'
    : issue.entity

  return (
    <div className="flex flex-col gap-4 p-2">
      <p className="text-theme-text text-sm">
        This {entityLabel} was edited by someone else while you were offline. Choose how to resolve:
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr>
              <th className="py-1 pr-3 text-xs font-semibold text-theme-text-muted">Field</th>
              <th className="py-1 pr-3 text-xs font-semibold text-theme-text">Your version</th>
              <th className="py-1 pr-1" />
              <th className="py-1 text-xs font-semibold text-theme-text">Server version</th>
            </tr>
          </thead>
          <tbody>
            {allKeys.map(k => (
              <FieldDiff
                key={k}
                field={k}
                client={clientObj?.[k]}
                server={serverObj?.[k]}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex gap-2 justify-end flex-wrap">
        <button
          onClick={() => keepMine(issue, onClose)}
          className="eclipse-btn-warning px-4 py-1.5 text-sm"
        >
          Keep mine
        </button>
        <button
          onClick={() => keepTheirs(issue, onClose)}
          className="eclipse-btn-secondary px-4 py-1.5 text-sm"
        >
          Keep theirs
        </button>
        <button
          onClick={onClose}
          className="eclipse-btn-primary px-4 py-1.5 text-sm"
        >
          Dismiss
        </button>
      </div>
    </div>
  )
}

export default ConflictResolutionModal
